import { getTime, millisecondsToSeconds } from "@suse-o11y/date-time";
import type { Time } from "@suse-o11y/date-time/time";
import { isNumber } from "@suse-o11y/is-number";
import type { JSON } from "@suse-o11y/json";
import { memoize } from "@suse-o11y/memoize";
import { type Failure, RemoteData, type Success } from "@suse-o11y/remote-data";
import { toJSON } from "@suse-o11y/to-json";
import { Tuple } from "@suse-o11y/tuple";
import { create, windowedFiniteBatchScheduler } from "@yornaath/batshit";
import { nanoid } from "nanoid";

import { InstantVector } from "~/models/instant-vector";
import { RangeMatrix } from "~/models/range-matrix";

import {
	type PromBatchInstantQuery,
	type PromBatchQueryItem,
	type PromBatchRangeQuery,
	postQueryBatchFn,
} from "../../../main/common/openapi";
import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { UseFullQueryOptions } from "../react-query";

interface BatchedPromQLRequest {
	/**
	 * When false, the particular instant/range promql request will use a personal http lookup.
	 * When true it is batched with other instant/range lookups within a short time window.
	 * When a JSON it will group batches by the stringified JSON as a key.
	 *
	 * The upside of batching is reducing total amount of http lookups,
	 * the downside is that the batched lookup is as slow as the slowest promql in the batch.
	 *
	 * @default true
	 */
	batch?: boolean | JSON;
}

const promqlBatcher = memoize((group?: string) =>
	create({
		fetcher: async (queries: ReadonlyArray<PromBatchQueryItem>) => {
			const { results } = await postQueryBatchFn(
				{
					body: { queries: queries.slice() },
				},
				// indicate the batching group calls in devtools
				{
					query: { group },
				},
			);

			return new Map(results.map((result) => [result.id, result]));
		},
		resolver: (results, query) => results.get(query.id),
		scheduler: windowedFiniteBatchScheduler({
			windowMs: 10,
			maxBatchSize: 200,
		}),
	}),
);

async function fetchQueryBatch(batched: JSON, query: PromBatchQueryItem) {
	if (batched === false) {
		const { results } = await postQueryBatchFn(
			{
				body: { queries: [query] },
			},
			{
				// indicate the none-batching calls in devtools
				query: { single: true },
			},
		);

		return results.find((result) => result.id === query.id);
	}

	if (batched === true) {
		return promqlBatcher().fetch(query);
	}

	const group = toJSON(batched);

	return promqlBatcher(group).fetch(query);
}

type InstantPromQLResult = Failure<string> | Success<Array<InstantVector>>;

interface InstantPromQLRequest
	extends BatchedPromQLRequest,
		Omit<PromBatchInstantQuery, "id" | "_type" | "time" | "step"> {
	timestamp?: Time;
	/**
	 * When string - it uses the promql step notation,
	 * when number - it is a milliseconds
	 */
	step: undefined | string | number;
}

const queryInstantPromQL = (({ batch = true, timestamp, ...request }) => {
	async function queryFn(): Promise<InstantPromQLResult> {
		const id = nanoid();

		const result = await fetchQueryBatch(batch, {
			...request,
			_type: "instant",
			id,
			time: timestamp == null ? undefined : millisecondsToSeconds(getTime(timestamp)).toString(),
			step: isNumber(request.step) ? millisecondsToSeconds(request.step).toString() : request.step?.toString(),
		});

		if (result?.resultType === "success" && result.data.data?.resultType === "vector") {
			const instant = result.data.data.result.map(InstantVector.fromUnix);

			return RemoteData.success(instant);
		}

		if (result?.resultType === "error") {
			return RemoteData.failure(result.error);
		}

		return RemoteData.failure("Requested query does not exist in response");
	}

	return {
		timestamp,
		structuralSharing: false,
		queryKey: Tuple("metrics", "instant", request),
		queryFn,
	} as const;
}) satisfies (request: InstantPromQLRequest) => UseFullQueryOptions;

interface RangePromQLRequest
	extends BatchedPromQLRequest,
		Omit<PromBatchRangeQuery, "id" | "_type" | "start" | "end" | "step"> {
	timeRange: TimeRange;
	/**
	 * When string - it uses the promql step notation,
	 * when number - it is a milliseconds
	 */
	step: string | number;
}

type RangePromQLResult = Failure<string> | Success<Array<RangeMatrix>>;

const queryRangePromQL = (({ batch = true, timeRange, ...request }) => {
	async function queryFn(): Promise<RangePromQLResult> {
		const id = nanoid();
		const { start, end } = timeRange.toInterval(Date.now());

		const result = await fetchQueryBatch(batch, {
			...request,
			_type: "range",
			id,
			start: millisecondsToSeconds(getTime(start)).toString(),
			end: millisecondsToSeconds(getTime(end)).toString(),
			step: isNumber(request.step) ? millisecondsToSeconds(request.step).toString() : request.step.toString(),
		});

		if (result?.resultType === "success" && result.data.data?.resultType === "matrix") {
			const range = result.data.data.result.map(RangeMatrix.fromUnix);

			return RemoteData.success(range);
		}

		if (result?.resultType === "error") {
			return RemoteData.failure(result.error);
		}

		return RemoteData.failure("Requested query does not exist in response");
	}

	return {
		timeRange,
		structuralSharing: false,
		queryKey: Tuple("metrics", "range", request),
		queryFn,
	} as const;
}) satisfies (request: RangePromQLRequest) => UseFullQueryOptions;

export type { BatchedPromQLRequest, InstantPromQLRequest, InstantPromQLResult, RangePromQLRequest, RangePromQLResult };
export { queryInstantPromQL, queryRangePromQL };
